import React, { useEffect, useContext, useState, useMemo } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import { Footer, Header, Aside, Main } from '../..';
import { AppContext, SettingsContextProvider, useAppSettingsContext } from '../../../contexts';
import { CircularProgress, Box } from "@mui/joy";
import { Backdrop } from "@mui/material";
import { DrawerProvider, useDrawer } from 'src/mtska-frontend-app-component/components/Drawer';
import ModuleMenu from 'src/mtska-frontend-app-component/components/Menu/ModuleMenu';
import { useStyleVariableInjector } from 'src/mtska-frontend-app-scaffolding/utils/StyleVariableInjector';
import JSONPretty from 'react-json-pretty';

const Full = () => {

    const { appConfiguration } = useContext(AppContext);

    return (
        <SettingsContextProvider>
            <DrawerProvider modules={appConfiguration.modules.active}  >
                <FullContent />
            </DrawerProvider>
        </SettingsContextProvider>
    );
};

const FullContent = () => {
    const {
        appConfiguration,
        setCurrentModuleName,
        setCurrentModuleNavigationData,
        setCurrentModuleScope,
        userCapabilities,
    } = useContext(AppContext)

    const { settings, settingsLoaded } = useAppSettingsContext();
    const { injectColorsToDocumentNode } = useStyleVariableInjector();
    const location = useLocation();
    const searchParams = new URLSearchParams(document.location.search)
    const { activate, hasRoute, routeIsInstack } = useDrawer();
    const [externalDrwHasBeenOpened, setExternalDrwHasBeenOpened] = useState(false);
    const [isOpen, setIsOpen] = useState();

    const findKeyDeep = (obj, targetKey) => {
        for (const key in obj) {
            if (key === targetKey) {
                return obj[key]; // valore associato alla chiave trovata
            }
            if (typeof obj[key] === 'object' && obj[key] !== null) {
                const result = findKeyDeep(obj[key], targetKey);
                if (result !== undefined) {
                    return result;
                }
            }
        }
        return undefined;
    }

    useEffect(() => {
        for (let [key, module] of Object.entries(appConfiguration.modules.active)) {
            if (!module.hasOwnProperty('menuItemScope') || !module.hasOwnProperty('menuItemName')) continue;

            let moduleMenuGroup = appConfiguration.navigation[module.menuItemScope];
            let moduleMenuItem = (moduleMenuGroup || {})[module.menuItemName]

            if (!moduleMenuItem) {
                //console.info('[AudienceModule] menuItem non trovato nello scope standard:', module.menuItemName);

                // cerco in tutto appConfiguration.navigation per vedere se il modulo è usato sotto uno scope diverso da quello standard, definito a livello di Module.jsx
                moduleMenuItem = findKeyDeep(appConfiguration.navigation, module.menuItemName);
                //console.info('[AudienceModule] menuItem trovato in scope alternativo:', moduleMenuItem);

                moduleMenuGroup = appConfiguration.navigation[moduleMenuItem.scope];
                //console.info('[AudienceModule] menuGroup identificato:', moduleMenuGroup);
            }

            if (moduleMenuItem == undefined) {
                console.info('ERRORE (Full): NON TROVATO', module.menuItemName, ' in ', module.menuItemScope, appConfiguration.navigation, ' Controlla se Module.jsx è fuori sincro con db');
            } else {
                if (moduleMenuItem && moduleMenuItem.href.split('?')[0] === location.pathname.split('?')[0]) {
                    setCurrentModuleName(moduleMenuItem.title);
                    const cms = appConfiguration.custom.menuCustomLabel.hasOwnProperty(moduleMenuItem.scope) ? appConfiguration.custom.menuCustomLabel[moduleMenuItem.scope] : moduleMenuItem.scope;
                    setCurrentModuleScope(cms);
                }
                if (moduleMenuGroup && moduleMenuItem.href.split('?')[0] === location.pathname.split('?')[0]) {
                    setCurrentModuleNavigationData(moduleMenuGroup);
                }
            }

        }
    }, [location]);

    useEffect(() => {
        const pref = sessionStorage.getItem('prefersAsideOpen');
        setIsOpen(pref === 'true' ?? true);
    }, []);

    useEffect(() => {
        if (searchParams.has('drw')) {
            try {
                const drw = searchParams.get("drw");
                const dwrData = JSON.parse(atob(drw));
                const routeName = dwrData.routeName;
                const params = dwrData.params;

                if (hasRoute(routeName) && !routeIsInstack(routeName) && !externalDrwHasBeenOpened) {
                    //console.info('Attivo', routeName, params);
                    activate(routeName, params);
                    setExternalDrwHasBeenOpened(true);
                }
            } catch (error) { }
        }
    }, [searchParams]);

    const handleIsOpen = (newIsOpen) => {
        setIsOpen(newIsOpen);
    }

    injectColorsToDocumentNode(appConfiguration.colors);

    const className = useMemo(() => {
        let cn = ['app'];
        cn.push(isOpen ? 'open' : 'closed');
        cn.push(userCapabilities['auth/role.is_driver'] ? 'driver' : '');
        cn.push(userCapabilities['auth/role.is_carmanager'] ? 'carmanager' : '');
        cn.push(userCapabilities['auth/role.is_fleetmanager'] ? 'fleetmanager' : '');
        cn.push(userCapabilities['auth/role.is_superuser'] ? 'superuser' : '');

        return cn.filter(cne => cne !== '').join(' ');
    }, [isOpen, userCapabilities]);

    return (
        (settingsLoaded) ? (
            <Box className={className}>

                <div className="superheader"></div>
                <Aside onOpenLayout={handleIsOpen} />
                <Header />
                <ModuleMenu />
                <Main>
                    {((settings) || (userCapabilities && userCapabilities['auth/role.is_driver'])) && <Outlet />}
                </Main>
                <Footer />

            </Box>
        ) : (
            <Backdrop open={true} sx={{ display: 'flex', flexDirection: 'column' }}>
                <CircularProgress />
                <Box sx={{ fontSize: '14px', margin: '30px 0', zIndex: '10000', color: 'white', display: 'inline-flex', borderRadius: '10px' }}>Loading User Settings</Box>
                {/* <JSONPretty data={settings} className={"json-wrapper"} /> */}
            </Backdrop>
        )
    );
};

export default Full;
